/**
 * GIS レイヤーのライブプレビュー地図 (docs/22 要件3、Sprint G2)。
 * 読み込んだ GeoJSON を admin 既定スタイルで地理院タイル (淡色) ラスタ上に重ね、スタイル保存後に即時反映する。
 * レイヤー spec は shared-types `buildGisMapLayers` を user overlay (G3) と共用する。
 */

import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl from 'maplibre-gl';
import { useEffect, useRef } from 'react';
import { buildGisMapLayers, resolveGisLayerStyle, type GisLayer } from '@citizen-walk/shared-types';
import { createArrowIconImage } from '@citizen-walk/shared-ui';
import { fetchGisGeoJson } from './useGisAdmin';

const GSI_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  // GIS ラベル (text-field を持つ symbol layer) の描画に必要な glyphs。無いと文字が出ない (docs/22 G8)。
  glyphs: 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf',
  sources: {
    gsi: {
      type: 'raster',
      tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '地理院タイル',
    },
  },
  layers: [{ id: 'gsi', type: 'raster', source: 'gsi' }],
};

interface Props {
  /** プレビューするレイヤー (displayOrder 昇順に積む)。 */
  layers: GisLayer[];
  className?: string;
}

export function GisPreviewMap({ layers, className }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const cacheRef = useRef<Map<string, GeoJSON.FeatureCollection>>(new Map());
  const fittedRef = useRef(false);

  // 地図初期化 (1 回)
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: GSI_STYLE,
      center: [139.767, 35.681],
      zoom: 9,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // レイヤー / スタイル変更時に再適用 (signature で diff)
  const signature = JSON.stringify(
    layers.map((l) => ({ id: l.id, gt: l.geometryType, sp: l.storagePath, s: l.style })),
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let cancelled = false;
    let loadHandler: (() => void) | null = null;

    const sorted = [...layers].sort((a, b) => a.displayOrder - b.displayOrder);

    const apply = async () => {
      const datas = await Promise.all(
        sorted.map(async (layer) => {
          let fc = cacheRef.current.get(layer.storagePath);
          if (!fc) {
            try {
              fc = await fetchGisGeoJson(layer.storagePath);
            } catch {
              fc = { type: 'FeatureCollection', features: [] };
            }
            cacheRef.current.set(layer.storagePath, fc);
          }
          return { layer, fc };
        }),
      );
      if (cancelled) return;

      const run = () => {
        if (cancelled || !mapRef.current) return;
        const m = mapRef.current;
        // 既存 GIS layer/source を撤去 (snapshot から)
        const style = m.getStyle();
        for (const lyr of style.layers ?? []) {
          if (lyr.id.startsWith('gis-') && m.getLayer(lyr.id)) m.removeLayer(lyr.id);
        }
        for (const sid of Object.keys(style.sources ?? {})) {
          if (sid.startsWith('gis-src-') && m.getSource(sid)) m.removeSource(sid);
        }
        // Tier2: 既存 GIS 矢印アイコン画像も撤去 (色変更を反映するため)。
        try {
          for (const imgId of m.listImages()) {
            if (imgId.startsWith('gis-') && imgId.endsWith('-arrowimg') && m.hasImage(imgId)) {
              m.removeImage(imgId);
            }
          }
        } catch {
          /* listImages 非対応環境は silent */
        }
        // 積み直し (昇順 = 後に追加したものが上)
        const bounds = new maplibregl.LngLatBounds();
        let hasBounds = false;
        for (const { layer, fc } of datas) {
          const sourceId = `gis-src-${layer.id}`;
          m.addSource(sourceId, { type: 'geojson', data: fc });
          const rstyle = resolveGisLayerStyle(layer.style);
          // Tier2: 方向矢印レイヤー用の矢じりアイコン (矢印色) を addImage。
          if (rstyle.arrowEnabled) {
            const imgId = `gis-${layer.id}-arrowimg`;
            try {
              // 上の listImages ループで撤去済のため通常 hasImage=false だが、撤去漏れ時の
              // 「image already exists」throw を避ける防御的チェック (reviewer 指摘の意図明示)。
              if (!m.hasImage(imgId)) {
                const img = createArrowIconImage(rstyle.arrowColor ?? rstyle.lineColor);
                if (img) m.addImage(imgId, img);
              }
            } catch {
              /* canvas 非対応は silent */
            }
          }
          const specs = buildGisMapLayers({
            idPrefix: `gis-${layer.id}`,
            sourceId,
            geometryType: layer.geometryType,
            style: rstyle,
          });
          for (const s of specs) m.addLayer(s.layer as unknown as maplibregl.AddLayerObject);
          if (layer.bbox) {
            bounds.extend([layer.bbox[0], layer.bbox[1]]);
            bounds.extend([layer.bbox[2], layer.bbox[3]]);
            hasBounds = true;
          }
        }
        if (hasBounds && !fittedRef.current) {
          map.fitBounds(bounds, { padding: 24, maxZoom: 16, duration: 0 });
          fittedRef.current = true;
        }
      };

      if (map.isStyleLoaded()) run();
      else {
        loadHandler = run;
        map.once('load', run);
      }
    };

    void apply();
    return () => {
      cancelled = true;
      if (loadHandler) {
        try {
          map.off('load', loadHandler);
        } catch {
          /* map 破棄後などは silent */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return (
    <div
      ref={containerRef}
      className={className ?? 'h-72 w-full overflow-hidden rounded-md border border-slate-200'}
    />
  );
}
